/**
 * HTTP Client
 *
 * Simple fetch wrapper for API calls with automatic authentication.
 * The auth token is obtained via an injected provider registered by AuthProvider —
 * this module does not access storage directly.
 */

import { getConfig } from '@/config/environment';
import { loggedFetch, FetchTarget } from '@/utils/logged-fetch';
import { getActiveRequestId } from '@/utils/request-context';

/**
 * Send the active client request id as `X-Request-ID` so server logs can be
 * joined with client logs. OFF on purpose: the API CORS config does not allow
 * (or expose) this header yet, so sending it would fail every preflight.
 * Flip to true once the API allows `X-Request-ID` in Access-Control-Allow-Headers
 * and exposes it in Access-Control-Expose-Headers (future API phase).
 */
const SEND_REQUEST_ID_HEADER = false;
const REQUEST_ID_HEADER = 'X-Request-ID';

export interface HttpClientOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number;
}

export class HttpClient {
  private baseUrl: string;
  private defaultTimeout: number;
  private tokenProvider: (() => string | null) | null = null;
  private unauthorizedHandler: (() => void) | null = null;

  constructor(baseUrl?: string) {
    const config = getConfig();
    this.baseUrl = baseUrl ?? config.serviceAPIUrl;
    this.defaultTimeout = config.apiTimeout;
  }

  /**
   * Register the function that provides the current auth token.
   * Called by the auth owner (AuthProvider) — HttpClient does not read storage directly.
   */
  setTokenProvider(provider: () => string | null): void {
    this.tokenProvider = provider;
  }

  /**
   * Register the function to call when the server returns 401 Unauthorized.
   * Called by the auth owner (AuthProvider) to trigger logout on session expiry.
   */
  setUnauthorizedHandler(handler: () => void): void {
    this.unauthorizedHandler = handler;
  }

  /**
   * Make HTTP request with automatic authentication.
   * If an auth token exists in localStorage, it's automatically included as a Bearer token.
   */
  async request<T>(
    endpoint: string,
    options: HttpClientOptions = {}
  ): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = this.defaultTimeout,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;

    // Build headers with conditional auth token
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Add Authorization header if token is available from the auth owner
    const authToken = this.tokenProvider?.() ?? null;
    if (authToken) {
      requestHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    if (SEND_REQUEST_ID_HEADER) {
      const requestId = getActiveRequestId();
      if (requestId) requestHeaders[REQUEST_ID_HEADER] = requestId;
    }

    // Logs one summary line per call (no bodies, no Authorization, no query).
    const response = await loggedFetch(
      url,
      {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
      },
      { timeoutMs: timeout, tagRequest: true, target: FetchTarget.API }
    );

    if (!response.ok) {
      if (response.status === 401) {
        this.unauthorizedHandler?.();
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, headers });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers });
  }

  /**
   * DELETE request
   */
  async delete<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }
}

// Singleton instance targeting the API service
export const httpClient = new HttpClient();
